import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { GenerateDeferredPayoutLinkResult } from "../api/deferred-payout";

/**
 * Composed deferred-payout origination draft. Persisted across the CIP redirect
 * so MintingPage can mint the payout-claim link when the sender returns.
 */
export interface IOriginationDraft {
  sourceCountry: string;
  destinationCountry: string;
  sourceCurrency: string;
  destinationCurrency: string;
  sendAmount: number;
  totalDebit: number;
  fundingMethod: string;
  /** Representative SKU for funding rail prepare (pinned at compose). */
  capabilityKey?: string;
  fundingPaymentMethodId?: string;
  recipientName: string;
}

/** Amount + funding captured on TransferPage before the recipient step. */
export type IComposeDraft = Omit<IOriginationDraft, "recipientName">;

interface OriginationState {
  draft: IOriginationDraft | null;
  setDraft: (draft: IOriginationDraft | null) => void;

  /** Partial compose from TransferPage; RecipientPage completes it. */
  composeDraft: IComposeDraft | null;
  setComposeDraft: (composeDraft: IComposeDraft | null) => void;

  /** Persists funding method while the user picks an instrument or adds a card. */
  pendingFundingMethod: string | null;
  setPendingFundingMethod: (method: string | null) => void;

  /**
   * Corridor selection on TransferPage. Survives the add-card round trip that
   * unmounts the page (same reason as pendingFundingMethod).
   */
  pendingSourceCountry: string | null;
  setPendingSourceCountry: (country: string | null) => void;
  pendingDestinationCountry: string | null;
  setPendingDestinationCountry: (country: string | null) => void;
  pendingDestinationCurrency: string | null;
  setPendingDestinationCurrency: (currency: string | null) => void;

  /** Persists recipient name across CIP. */
  pendingRecipientName: string;
  setPendingRecipientName: (name: string) => void;

  mintResult: GenerateDeferredPayoutLinkResult | null;
  setMintResult: (result: GenerateDeferredPayoutLinkResult | null) => void;

  reset: () => void;
}

export const useRequestStore = create<OriginationState>()(
  persist(
    (set) => ({
      draft: null,
      setDraft: (draft) => set(() => ({ draft })),
      composeDraft: null,
      setComposeDraft: (composeDraft) => set(() => ({ composeDraft })),
      pendingFundingMethod: null,
      setPendingFundingMethod: (pendingFundingMethod) =>
        set(() => ({ pendingFundingMethod })),
      pendingSourceCountry: null,
      setPendingSourceCountry: (pendingSourceCountry) =>
        set(() => ({ pendingSourceCountry })),
      pendingDestinationCountry: null,
      setPendingDestinationCountry: (pendingDestinationCountry) =>
        set(() => ({ pendingDestinationCountry })),
      pendingDestinationCurrency: null,
      setPendingDestinationCurrency: (pendingDestinationCurrency) =>
        set(() => ({ pendingDestinationCurrency })),
      pendingRecipientName: "",
      setPendingRecipientName: (pendingRecipientName) =>
        set(() => ({ pendingRecipientName })),
      mintResult: null,
      setMintResult: (mintResult) => set(() => ({ mintResult })),
      reset: () =>
        set(() => ({
          draft: null,
          composeDraft: null,
          mintResult: null,
          pendingFundingMethod: null,
          pendingSourceCountry: null,
          pendingDestinationCountry: null,
          pendingDestinationCurrency: null,
          pendingRecipientName: "",
        })),
    }),
    { name: "raas-widgets-flow-deferred-payout-origination-draft" },
  ),
);
